import { useRef, useState } from 'react'

interface AppleWalletHtmlEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

const AppleWalletHtmlEditor = ({ value, onChange, placeholder }: AppleWalletHtmlEditorProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [showUrlInput, setShowUrlInput] = useState(false)
  const [urlInput, setUrlInput] = useState('')
  const [linkText, setLinkText] = useState('')

  const insertLink = () => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = value.substring(start, end)

    // Set the link text to selected text or default
    setLinkText(selectedText || 'link text')
    setShowUrlInput(true)
  }

  const handleInsertLink = () => {
    if (!urlInput.trim()) return

    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = value.substring(start, end)

    const linkTextToUse = selectedText || linkText || 'link text'
    const newValue = `${value.substring(0, start)}<a href="${urlInput.trim()}">${linkTextToUse}</a>${value.substring(
      end
    )}`
    onChange(newValue)

    // Reset focus and cursor position
    setTimeout(() => {
      textarea.focus()
      const newPosition = start + `<a href="${urlInput.trim()}">${linkTextToUse}</a>`.length
      textarea.setSelectionRange(newPosition, newPosition)
    }, 0)

    // Reset state
    setShowUrlInput(false)
    setUrlInput('')
    setLinkText('')
  }

  const handleCancelLink = () => {
    setShowUrlInput(false)
    setUrlInput('')
    setLinkText('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && urlInput.trim()) {
      e.preventDefault()
      handleInsertLink()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancelLink()
    }
  }

  return (
    <div style={{ margin: '20px 0' }}>
      <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
        <strong>Apple Wallet Content Editor:</strong> Select text and use the link button below to add anchor tags.
      </div>

      {/* Toolbar */}
      <div
        style={{
          marginBottom: '10px',
          padding: '8px',
          border: '1px solid #d1d5db',
          borderRadius: '4px 4px 0 0',
          backgroundColor: '#f9fafb',
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={insertLink}
          style={{
            padding: '6px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
          }}
          title="Add link to selected text"
        >
          🔗 Add Link
        </button>
      </div>

      {/* URL Input Modal */}
      {showUrlInput && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={handleCancelLink}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              minWidth: '400px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 'bold' }}>Add Link</h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Link Text:
              </label>
              <input
                type="text"
                value={linkText}
                onChange={e => setLinkText(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
                placeholder="Enter link text"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>URL:</label>
              <input
                type="url"
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
                placeholder="https://example.com"
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCancelLink}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInsertLink}
                disabled={!urlInput.trim()}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #3b82f6',
                  borderRadius: '4px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  cursor: urlInput.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  opacity: urlInput.trim() ? 1 : 0.5,
                }}
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Text Editor */}
      <textarea
        ref={textareaRef}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Enter content for the back of the Apple Wallet pass...'}
        style={{
          width: '100%',
          minHeight: '200px',
          padding: '12px',
          border: '1px solid #d1d5db',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          fontSize: '14px',
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
          resize: 'vertical',
          outline: 'none',
        }}
        onFocus={e => {
          e.target.style.border = '1px solid #3b82f6'
          e.target.style.borderTop = 'none'
        }}
        onBlur={e => {
          e.target.style.border = '1px solid #d1d5db'
          e.target.style.borderTop = 'none'
        }}
      />
    </div>
  )
}

export default AppleWalletHtmlEditor
